import {useRef, useState} from "react";

/**
 * A very short enter delay is used to allow users to "skip" over navigation
 * elements without it feeling too laggy.
 * A longer exit delay is used because:
 * https://baymard.com/blog/dropdown-menu-flickering-issue#a-hover-delay-of-300500-ms-alleviates-the-worst-of-the-flickering-issue
 * https://www.w3.org/WAI/tutorials/menus/flyout/#mouse-users
 */
export const MOUSE_ENTER_DELAY = 100;
export const MOUSE_LEAVE_DELAY = 300;

export type UseHoverMenus = {
	activeSubmenu: string | null;
	isSubmenuOpen: boolean;
	onNavItemMouseEnter: () => void;
	onNavItemMouseLeave: () => void;
	onDropdownItemMouseEnter: (itemId: string) => () => void;
	onDropdownItemMouseLeave: () => void;
};

const useHoverMenus: (options?: {
	mouseEnterDelayOverride?: number;
	mouseLeaveDelayOverride?: number;
}) => UseHoverMenus = (options = {}) => {
	/**
	 * track the currently active submenu and whether or not it's open.
	 * tracking the open submenu separately lets us do dumb things like open
	 * the next submenu over without a delay.
	 * if we find this behaviour undesirable, we can track this state with just
	 * `activeSubmenu`.
	 **/
	const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
	const [isSubmenuOpen, setIsSubmenuOpen] = useState<boolean>(false);

	/**
	 * two separate timers because we want the dropdown to close normally if a
	 * user "skips" over a nav item, but we want it to close almost instantly
	 * when a nav item is deliberately focused.
	 */
	const navItemHoverDelayTimer = useRef<NodeJS.Timeout | null>(null);
	const dropdownItemHoverDelayTimer = useRef<NodeJS.Timeout | null>(null);

	const onNavItemMouseEnter = () => {
		if (isSubmenuOpen) {
			navItemHoverDelayTimer.current = setTimeout(() => {
				if (dropdownItemHoverDelayTimer.current) {
					clearTimeout(dropdownItemHoverDelayTimer.current);
				}
				setActiveSubmenu(null);
				setIsSubmenuOpen(false);
			}, options?.mouseEnterDelayOverride ?? MOUSE_ENTER_DELAY);
		}
	};

	const onNavItemMouseLeave = () => {
		if (navItemHoverDelayTimer.current) {
			clearTimeout(navItemHoverDelayTimer.current);
		}
	};

	const onDropdownItemMouseEnter = (itemId: string) => {
		return () => {
			setActiveSubmenu(itemId);
			if (dropdownItemHoverDelayTimer.current) {
				clearTimeout(dropdownItemHoverDelayTimer.current);
			}

			if (!isSubmenuOpen) {
				dropdownItemHoverDelayTimer.current = setTimeout(() => {
					setIsSubmenuOpen(true);
				}, options?.mouseEnterDelayOverride ?? MOUSE_ENTER_DELAY);
			}
		};
	};
	const onDropdownItemMouseLeave = () => {
		if (dropdownItemHoverDelayTimer.current) {
			clearTimeout(dropdownItemHoverDelayTimer.current);
		}

		dropdownItemHoverDelayTimer.current = setTimeout(() => {
			setIsSubmenuOpen(false);
			setActiveSubmenu(null);
		}, options?.mouseLeaveDelayOverride ?? MOUSE_LEAVE_DELAY);
	};

	return {
		activeSubmenu,
		isSubmenuOpen,
		onNavItemMouseEnter,
		onNavItemMouseLeave,
		onDropdownItemMouseEnter,
		onDropdownItemMouseLeave,
	};
};

export default useHoverMenus;
